/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IAzureBenefitRequest } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequest";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE,
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE,
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE,
  AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE,
  AZURE_BENEFIT_REQUESTS_SEARCH,
  AZURE_BENEFIT_REQUESTS_SEARCH_FAIL,
  AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS,
  GET_AZURE_BENEFIT_REQUEST_DETAILS,
  GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL,
  GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS,
} from "./AzureBenefitRequestsActionTypes";

/**
 * Action for when searching azure benefit requests.
 * @interface
 */
export interface ISearchAzureBenefitRequests {
  type: typeof AZURE_BENEFIT_REQUESTS_SEARCH;
  searchText: string;
  selectedRequestsFilterValues: IFilterValue[];
  pageSize: number;
  pageIndex: number;
  tenant: string;
}

/**
 * Action for when requests were searched successfully.
 * @interface
 */
export interface ISearchAzureBenefitRequestsSuccess {
  type: typeof AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS;
  requests: IAzureBenefitRequest[];
  pageIndex: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading requests failed.
 * @interface
 */
export interface ISearchAzureBenefitRequestsFail {
  type: typeof AZURE_BENEFIT_REQUESTS_SEARCH_FAIL;
  error: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IAzureBenefitRequestsFilterValueAdd {
  type: typeof AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IAzureBenefitRequestsFilterValueRemove {
  type: typeof AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IAzureBenefitRequestsFilterValueUpdate {
  type: typeof AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when clearing selected filter values.
 * @interface
 */
export interface IAzureBenefitRequestsFilterValueClear {
  type: typeof AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR;
}

/**
 * Action for getting the details of an individual azure
 * benefit request.
 */
export interface IGetAzureBenefitRequestDetails {
  type: typeof GET_AZURE_BENEFIT_REQUEST_DETAILS;
  userProfileId: number;
  tenant: string;
}

/**
 * Action for getting the details of an individual azure
 * benefit request.
 */
export interface IGetAzureBenefitRequestDetailsSuccess {
  type: typeof GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS;
  questionAnswerMap: { [key: string]: string };
  expedited: boolean;
  expeditedDate?: Date;
}

/**
 * Action for getting the details of an individual azure
 * benefit request.
 */
export interface IGetAzureBenefitRequestDetailsFail {
  type: typeof GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL;
  error: string;
}

/**
 * Action for approving or declining an azure benefit request.
 */
export interface IApproveOrDeclineAzureBenefitRequest {
  type: typeof AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE;
  approveOrDecline: boolean;
  requestId: number;
  tenant: string;
}

/**
 * Action for approving or declining an azure benefit request failure.
 */
export interface IApproveOrDeclineAzureBenefitRequestFailure {
  type: typeof AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE;
  errorMessage: string;
}

/**
 * Action for approving or declining an azure benefit request success.
 */
export interface IApproveOrDeclineAzureBenefitRequestSuccess {
  type: typeof AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS;
  updated: boolean;
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addAzureBenefitRequestsFilterValue(
  filterValue: IFilterValue
): IAzureBenefitRequestsFilterValueAdd {
  return {
    type: AZURE_BENEFIT_REQUESTS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeAzureBenefitRequestsFilterValue(
  filterValue: IFilterValue
): IAzureBenefitRequestsFilterValueRemove {
  return {
    type: AZURE_BENEFIT_REQUESTS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateAzureBenefitRequestsFilterValue(
  filterValue: IFilterValue
): IAzureBenefitRequestsFilterValueUpdate {
  return {
    type: AZURE_BENEFIT_REQUESTS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_SEARCH.
 */
export function searchAzureBenefitRequests(
  searchText: string,
  selectedRequestsFilterValues: IFilterValue[],
  pageIndex: number,
  pageSize: number,
  tenant: string
): ISearchAzureBenefitRequests {
  return {
    type: AZURE_BENEFIT_REQUESTS_SEARCH,
    searchText,
    selectedRequestsFilterValues,
    pageSize,
    pageIndex,
    tenant,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS.
 * @param requests the azure benefit requests successfully searched.
 */
export function searchAzureBenefitRequestsSuccess(
  requests: IAzureBenefitRequest[],
  pageIndex: number,
  pageSize: number,
  filteredCount: number
): ISearchAzureBenefitRequestsSuccess {
  return {
    type: AZURE_BENEFIT_REQUESTS_SEARCH_SUCCESS,
    requests,
    pageIndex,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchAzureBenefitRequestsFail(error: string): ISearchAzureBenefitRequestsFail {
  return {
    type: AZURE_BENEFIT_REQUESTS_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for GET_AZURE_BENEFIT_REQUEST_DETAILS.
 */
export function getAzureBenefitRequestDetails(
  userProfileId: number,
  tenant: string
): IGetAzureBenefitRequestDetails {
  return {
    type: GET_AZURE_BENEFIT_REQUEST_DETAILS,
    userProfileId,
    tenant,
  };
}

/**
 * Action creator for GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS.
 */
export function getAzureBenefitRequestDetailsSuccess(
  questionAnswerMap: { [key: string]: string },
  expedited: boolean,
  expeditedDate?: Date
): IGetAzureBenefitRequestDetailsSuccess {
  return {
    type: GET_AZURE_BENEFIT_REQUEST_DETAILS_SUCCESS,
    questionAnswerMap,
    expedited,
    expeditedDate,
  };
}

/**
 * Action creator for GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL.
 * @param error the error message from the API.
 */
export function getAzureBenefitRequestDetailsFail(
  error: string
): IGetAzureBenefitRequestDetailsFail {
  return {
    type: GET_AZURE_BENEFIT_REQUEST_DETAILS_FAIL,
    error,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE.
 */
export function approveOrDeclineAzureBenefitRequest(
  approveOrDecline: boolean,
  requestId: number,
  tenant: string
): IApproveOrDeclineAzureBenefitRequest {
  return {
    type: AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE,
    approveOrDecline,
    requestId,
    tenant,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS.
 */
export function approveOrDeclineAzureBenefitRequestSuccess(
  updated: boolean
): IApproveOrDeclineAzureBenefitRequestSuccess {
  return {
    type: AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_SUCCESS,
    updated,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE.
 * @param errorMessage the error message from the API.
 */
export function approveOrDeclineAzureBenefitRequestFailure(
  errorMessage: string
): IApproveOrDeclineAzureBenefitRequestFailure {
  return {
    type: AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE_FAILURE,
    errorMessage,
  };
}

/**
 * Action creator for AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR.
 * @returns {IAzureBenefitRequestsFilterValueClear}
 */
export function clearAzureBenefitRequestsFilterValues(): IAzureBenefitRequestsFilterValueClear {
  return {
    type: AZURE_BENEFIT_REQUESTS_FILTER_VALUE_CLEAR,
  };
}

export type AzureBenefitRequestsActionTypes =
  | ISearchAzureBenefitRequests
  | ISearchAzureBenefitRequestsSuccess
  | ISearchAzureBenefitRequestsFail
  | IAzureBenefitRequestsFilterValueAdd
  | IAzureBenefitRequestsFilterValueRemove
  | IAzureBenefitRequestsFilterValueClear
  | IAzureBenefitRequestsFilterValueUpdate
  | IGetAzureBenefitRequestDetails
  | IGetAzureBenefitRequestDetailsFail
  | IGetAzureBenefitRequestDetailsSuccess
  | IApproveOrDeclineAzureBenefitRequest
  | IApproveOrDeclineAzureBenefitRequestFailure
  | IApproveOrDeclineAzureBenefitRequestSuccess;
